'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { usePinkLeopardTracking } from '../hooks/usePinkLeopardTracking';

interface BannerProps {
  bannerData: {
    image: string;
    title?: string; // PC端使用
    word?: string; // WAP端使用
    description?: string; // PC端使用
    aspectRatio?: string; // 宽高比
    jump_type?: number; // 跳转类型：1-商品列表，2-商品详情
    jump_value?: string; // 跳转值
  };
  deviceType: EPlatform;
}

export default function Banner({ bannerData, deviceType }: BannerProps) {
  const { trackBannerExposure, trackBannerClick } = usePinkLeopardTracking();
  const tImages = useTranslations('activity.pinkLeopardGlasses.images');
  const { navigate } = useAppNavigation();

  const { ref, inView } = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 处理曝光埋点 - 与Vue版本一致
  useEffect(() => {
    if (inView) {
      trackBannerExposure('banner1');
    }
  }, [inView, trackBannerExposure]);

  // 处理Banner跳转逻辑 - 与Vue版本一致
  const handleBannerClick = () => {
    if (!bannerData.jump_type || !bannerData.jump_value) return;

    // 触发点击埋点
    trackBannerClick('banner1');

    if (bannerData.jump_type === 2) {
      // 跳转到商品详情页
      navigate(`/goods-detail/${bannerData.jump_value}`);
    } else {
      // 跳转到商品列表页
      navigate(`/goods-list/${bannerData.jump_value}`);
    }
  };

  const isWeb = deviceType === EPlatform.WEB;
  const aspectRatio = bannerData.aspectRatio || (isWeb ? '1920/580' : '749/460');

  // 如果没有图片，显示占位区域 - 与Vue版本一致
  if (!bannerData.image) {
    return <div className="banner-render w-full bg-gray-200" style={{ aspectRatio }} />;
  }

  const BannerContent = () => (
    <div
      className="banner-wrap relative w-full h-full"
      onClick={bannerData.jump_type ? handleBannerClick : undefined}
    >
      <Image
        src={bannerData.image}
        alt={tImages('bannerAlt')}
        fill
        className="block w-full h-auto object-cover"
        priority
        sizes="100vw"
      />

      {/* WAP端：标题在右上角 */}
      {!isWeb && bannerData.word && (
        <h1 className="absolute top-[42px] right-0 w-[175px] font-bb text-[30px] text-white text-center leading-[25px]">
          {bannerData.word}
        </h1>
      )}

      {/* PC端：文字框在左下角 - 使用断点适配 */}
      {isWeb && (bannerData.title || bannerData.description) && (
        <div
          className="word-box absolute text-white
          md:bottom-[60px] md:left-[60px] md:w-[360px]
          lg:bottom-[80px] lg:left-[80px] lg:w-[480px]
          xl:bottom-[100px] xl:left-[100px] xl:w-[580px]
          2xl:bottom-[110px] 2xl:left-[110px] 2xl:w-[650px]
          3xl:bottom-[123px] 3xl:left-[124px] 3xl:w-[718px]"
        >
          {bannerData.description && (
            <p
              className="description text-center mb-[18px] pl-[24px]
                md:text-[12px] md:leading-[20px] md:mb-[9px] md:pl-[12px]
                lg:text-[16px] lg:leading-[26px] lg:mb-[12px] lg:pl-[16px]
                xl:text-[20px] xl:leading-[32px] xl:mb-[15px] xl:pl-[20px]
                2xl:text-[22px] 2xl:leading-[36px] 2xl:mb-[16px] 2xl:pl-[22px]
                3xl:text-[24px] 3xl:leading-[40px] 3xl:mb-[18px] 3xl:pl-[24px]"
              dangerouslySetInnerHTML={{ __html: bannerData.description }}
            />
          )}
          {bannerData.title && (
            <h1
              className="title font-bs text-center
              md:text-[40px] md:leading-[36px]
              lg:text-[54px] lg:leading-[48px]
              xl:text-[68px] xl:leading-[60px]
              2xl:text-[76px] 2xl:leading-[68px]
              3xl:text-[86px] 3xl:leading-[76px]"
            >
              {bannerData.title}
            </h1>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div ref={ref} className="fit-banner w-full">
      <div className="banner relative w-full cursor-pointer" style={{ aspectRatio }}>
        {bannerData.jump_type ? (
          <Link
            href={
              bannerData.jump_type === 2
                ? `/goods-detail/${bannerData.jump_value}`
                : `/goods-list/${bannerData.jump_value}`
            }
            aria-label="pink leopard glasses banner"
            className="banner-wrap block w-full h-full cursor-pointer"
            onClick={(e) => {
              e.preventDefault(); // 阻止默认跳转
              handleBannerClick();
            }}
          >
            <BannerContent />
          </Link>
        ) : (
          <BannerContent />
        )}
      </div>
    </div>
  );
}
